// src/components/AppRoutes.jsx

import {
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Box,
  Heading,
  Text,
  useToast,
} from "@chakra-ui/react";

// Public pages
import Login from "../pages/Login";
import Signup from "../pages/business/Signup";
import SignupSuccess from "../pages/SignupSuccess";
import RedeemPage from "../pages/RedeemPage";
import CouponLanding from "../pages/CouponLanding";
import CouponPage from "../pages/CouponPage";
import Debug from "../pages/Debug";

// Public homepage and flyer discovery
import HomePage from "../pages/HomePage";
import BusinessPage from "../pages/BusinessPage";
import PricingPage from "../pages/PricingPage";
import FindFlyer from "../pages/FindFlyer";
import PublicFlyerPage from "../pages/PublicFlyerPage";
import SectorOverview from "../pages/SectorOverview";

// Legal pages
import PrivacyPolicy from "../pages/PrivacyPolicy";
import CookiePolicy from "../pages/CookiePolicy";
import Terms from "../pages/Terms";

// QR landing page
import BusinessLanding from "../pages/BusinessLanding";

// Public map
import PostcodeCouponsMap from "../components/PostcodeCouponsMap";

// Protected routing
import ProtectedRoute from "./ProtectedRoute";

// Business cockpit pages
import AccountPage from "../pages/AccountPage";
import MyFlyersPage from "../pages/MyFlyersPage";
import MyBookingsPage from "../pages/MyBookingsPage";
import InvoicesPage from "../pages/InvoicesPage";
import CreditHistoryPage from "../pages/CreditHistoryPage";

// Dashboard layout and pages
import DashboardRouter from "./DashboardRouter";
import CampaignDashboard from "./CampaignDashboard";
import QrAnalyticsDashboard from "./analytics/QrAnalyticsDashboard";

// Admin and referral pages
import ReferralDashboard from "../components/ReferralDashboard";
import AdminCockpit from "../pages/admin/AdminCockpit";

// Public layout
import PublicLayout from "../components/PublicLayout";

// Print page
import PrintPage from "../pages/print";

// Booking page
import BusinessSlotBooking from "../pages/BusinessSlotBooking";

const isDev = import.meta.env.DEV;

// -------------------------------------------------------
// 404
// -------------------------------------------------------

function NotFound() {
  return (
    <Box
      p="2rem"
      textAlign="center"
    >
      <Heading size="lg">
        404
      </Heading>

      <Text mt={2}>
        Sorry, the page you’re looking for doesn’t exist.
      </Text>
    </Box>
  );
}

// -------------------------------------------------------
// ROUTE LOGGER
// -------------------------------------------------------

function RouteLogger() {
  const location = useLocation();
  const toast = useToast();
  const lastPath = useRef("");

  useEffect(() => {
    const debug =
      import.meta.env.VITE_DEBUG_TOASTS === "true";

    if (
      location.pathname ===
      lastPath.current
    ) {
      return;
    }

    lastPath.current =
      location.pathname;

    if (isDev) {
      console.log(
        "[ROUTE]",
        location.pathname
      );
    }

    if (debug) {
      toast({
        title: "Navigated",
        description:
          location.pathname,
        status: "info",
        duration: 1500,
        isClosable: true,
      });
    }
  }, [
    location.pathname,
    toast,
  ]);

  return null;
}

// -------------------------------------------------------
// PUBLIC MAP WRAPPER
// -------------------------------------------------------

function PublicMapWrapper() {
  const [
    region,
    setRegion,
  ] = useState(() => {
    const saved =
      sessionStorage.getItem(
        "mapRegion"
      );

    if (
      !saved ||
      !/^[A-Z]{1,2}$/.test(saved)
    ) {
      console.warn(
        "[PublicMapWrapper] Invalid saved region — using PR"
      );

      sessionStorage.setItem(
        "mapRegion",
        "PR"
      );

      return "PR";
    }

    return saved;
  });

  useEffect(() => {
    if (
      !region ||
      !/^[A-Z]{1,2}$/.test(region)
    ) {
      console.warn(
        `[PublicMapWrapper] Invalid region '${region}' — falling back to PR`
      );

      sessionStorage.setItem(
        "mapRegion",
        "PR"
      );

      setRegion("PR");
      return;
    }

    sessionStorage.setItem(
      "mapRegion",
      region
    );

    if (isDev) {
      console.log(
        "[PublicMapWrapper] region state:",
        region
      );
    }
  }, [region]);

  return (
    <PostcodeCouponsMap
      authId={undefined}
      flyerId={null}
      region={region}
      setRegionOverride={
        setRegion
      }
    />
  );
}

// -------------------------------------------------------
// PROTECTED COCKPIT WRAPPER
// -------------------------------------------------------

function ProtectedCockpitLayout({
  user,
}) {
  return (
    <ProtectedRoute>
      <DashboardRouter
        user={user}
      />
    </ProtectedRoute>
  );
}

// -------------------------------------------------------
// ROUTES
// -------------------------------------------------------

export default function AppRoutes({
  user,
}) {
  return (
    <>
      <RouteLogger />

      <Routes>
        {/* PRINT PAGE */}

        <Route
          path="/print"
          element={<PrintPage />}
        />

        {/* =====================================================
            PUBLIC ROUTES
        ===================================================== */}

        <Route
          path="/"
          element={
            <PublicLayout>
              <HomePage />
            </PublicLayout>
          }
        />

        <Route
          path="/coupon-ads"
          element={
            <PublicLayout>
              <BusinessPage />
            </PublicLayout>
          }
        />

        {/* PRICING
            PricingPage already contains PublicLayout internally. */}

        <Route
          path="/pricing"
          element={<PricingPage />}
        />

        {/* =====================================================
            LEGAL ROUTES
            These pages contain PublicLayout internally.
        ===================================================== */}

        <Route
          path="/privacy"
          element={<PrivacyPolicy />}
        />

        <Route
          path="/cookies"
          element={<CookiePolicy />}
        />

        <Route
          path="/terms"
          element={<Terms />}
        />

        {/* BUSINESS SIGNUP REDIRECT */}

        <Route
          path="/business/signup"
          element={
            <Navigate
              to="/signup?business=true"
              replace
            />
          }
        />

        <Route
          path="/signup"
          element={<Signup />}
        />

        <Route
          path="/login"
          element={
            <PublicLayout>
              <Login />
            </PublicLayout>
          }
        />

        <Route
          path="/signup/success"
          element={
            <PublicLayout>
              <SignupSuccess />
            </PublicLayout>
          }
        />

        {/* FIND COUPONS */}

        <Route
          path="/find"
          element={
            <PublicLayout>
              <FindFlyer />
            </PublicLayout>
          }
        />

        {/* PUBLIC FLYER */}

        <Route
          path="/flyer/:sector"
          element={
            <PublicLayout>
              <PublicFlyerPage />
            </PublicLayout>
          }
        />

        {/* BUSINESS QR LANDING */}

        <Route
          path="/b/:slug"
          element={
            <PublicLayout>
              <BusinessLanding />
            </PublicLayout>
          }
        />

        {/* PUBLIC MAP */}

        <Route
          path="/map"
          element={
            <PublicLayout>
              <PublicMapWrapper />
            </PublicLayout>
          }
        />

        {/* SECTOR */}

        <Route
          path="/sector/:sector"
          element={
            <PublicLayout>
              <SectorOverview />
            </PublicLayout>
          }
        />

        {/* COUPONS */}

        <Route
          path="/coupon/:couponId"
          element={
            <PublicLayout>
              <CouponLanding />
            </PublicLayout>
          }
        />

        <Route
          path="/coupons/:postcode"
          element={
            <PublicLayout>
              <CouponPage />
            </PublicLayout>
          }
        />

        {/* REDEMPTION */}

        <Route
          path="/r/:token"
          element={
            <PublicLayout>
              <RedeemPage />
            </PublicLayout>
          }
        />

        <Route
          path="/redeem"
          element={
            <PublicLayout>
              <RedeemPage />
            </PublicLayout>
          }
        />

        {/* DEBUG */}

        <Route
          path="/debug"
          element={
            <PublicLayout>
              <Debug />
            </PublicLayout>
          }
        />

        {/* =====================================================
            PROTECTED COCKPIT ROUTES
        ===================================================== */}

        <Route
          element={
            <ProtectedCockpitLayout
              user={user}
            />
          }
        >
          <Route
            path="/dashboard"
            element={
              <CampaignDashboard />
            }
          />

          {/* QR ANALYTICS */}

          <Route
            path="/dashboard/qr-analytics"
            element={
              <QrAnalyticsDashboard
                user={user}
              />
            }
          />

          {/* ACCOUNT */}

          <Route
            path="/account"
            element={
              <AccountPage />
            }
          />

          {/* FLYERS */}

          <Route
            path="/my-flyers"
            element={
              <MyFlyersPage />
            }
          />

          {/* BOOKINGS */}

          <Route
            path="/my-bookings"
            element={
              <MyBookingsPage />
            }
          />

          {/* INVOICES */}

          <Route
            path="/invoices"
            element={
              <InvoicesPage />
            }
          />

          {/* CREDIT */}

          <Route
            path="/credit-history"
            element={
              <CreditHistoryPage />
            }
          />

          {/* REFERRALS */}

          <Route
            path="/referrals"
            element={
              <ReferralDashboard />
            }
          />

          {/* ADMIN */}

          <Route
            path="/admin/cockpit"
            element={
              <AdminCockpit />
            }
          />
        </Route>

        {/* =====================================================
            PROTECTED BOOKING WORKFLOW
        ===================================================== */}

        <Route
          path="/business/slot/:flyerId/:slotNumber/book"
          element={
            <ProtectedRoute>
              <BusinessSlotBooking />
            </ProtectedRoute>
          }
        />

        {/* =====================================================
            404
        ===================================================== */}

        <Route
          path="*"
          element={
            <PublicLayout>
              <NotFound />
            </PublicLayout>
          }
        />
      </Routes>
    </>
  );
}

console.log(
  "force rebuild 7"
);